<template>
    <div class="sticky-card">
        <v-card flat>
            <v-card-text>
                <div class="d-flex align-center mb-4">
                    <v-avatar
                        v-if="app.comp_logo_blob_urls?.length"
                        size="80"
                        rounded
                        class="avatar-image mr-4"
                    >
                        <v-img
                            :src="app.comp_logo_blob_urls[0]"
                            alt=""
                            contain
                        />
                    </v-avatar>

                    <v-avatar
                        v-else
                        size="80"
                        color="primary"
                        rounded
                        class="mr-4"
                    >
                        <v-icon
                            color="white"
                            size="40"
                            aria-hidden="true"
                        >
                            mdi-application
                        </v-icon>
                    </v-avatar>

                    <div class="flex-grow-1">
                        <h1 class="text-h5 font-weight-bold mb-0 line-height-1">{{ app_label }}</h1>

                        <p class="text-subtitle-1 mb-0">
                            By {{ app.app_owner_organization_label || 'Unknown Developer' }}
                        </p>
                    </div>
                </div>

                <m-showdown
                    class="text-body-1 ma-0"
                    :value="app.app_description_short_md"
                />

                <v-divider class="mb-4" />

                <div
                    class="mb-4"
                    v-if="app_price_is_set"
                >
                    <div
                        v-if="app_is_free"
                        class="text-center my-6"
                    >
                        <div class="text-h4 primary--text font-weight-bold">Free</div>
                        <p class="text-caption mb-0">No cost to install or use</p>
                    </div>

                    <div v-else>
                        <div class="text-center my-6">
                            <div class="text-h4 primary--text font-weight-bold ">
                                {{ app_monthly_fee_pretty }}
                            </div>

                            <p v-if="app_is_installed && app_is_in_free_trial">
                                {{ app_trial_status_message }}
                            </p>

                            <v-alert
                                v-if="app_is_in_free_trial"
                                border="left"
                                colored-border
                                color="success"
                                dense
                                class="shaded-light mt-3"
                            >
                                <div class="text-left text-body-2 pl-1">
                                    <strong>Free Trial Active</strong>
                                    <br />
                                    Billing will automatically begin after your trial ends. You can cancel any time
                                    during the trial period to avoid charges.
                                </div>
                            </v-alert>

                            <div
                                v-else-if="app_is_installed && app.installed_time"
                                class="text-body-2 mt-1 d-flex justify-center align-center success--text font-weight-bold"
                            >
                                <v-icon
                                    small
                                    class="mr-1 success--text"
                                >mdi-check-circle</v-icon>
                                Installed on {{ installed_time_pretty }}
                            </div>
                        </div>

                        <v-alert
                            v-if="!app_is_installed && app.free_trial_days_remaining"
                            border="left"
                            colored-border
                            color="primary"
                            class="shaded-light pl-5 mb-3"
                        >
                            <div class="text-body-2">
                                <strong>{{ app.free_trial_days_remaining }}-day free trial</strong>
                                <br />
                                Try all features at no cost, then pay {{ app_monthly_fee_pretty }}.
                            </div>
                        </v-alert>
                    </div>
                </div>

                <v-alert
                    v-if="!app_is_approved"
                    type="error"
                    icon="mdi-xml"
                    border="left"
                    colored-border
                    class="my-6 shaded-light"
                    dense
                >
                    <div class="font-weight-bold mb-0">In Development</div>
                    <div class="text-body-2 mb-0">
                        This app is not yet approved. It is only visible to your organization until it's approved.
                    </div>
                </v-alert>

                <template v-if="$rc.$state.isOrganizationAdmin()">
                    <div class="mt-6">
                        <app-install-btn
                            :app="app"
                            :dense="false"
                            @installed="reloader"
                        />
                    </div>

                    <v-btn
                        v-if="app_is_installed && !app_is_in_free_trial"
                        color="error"
                        class="my-2"
                        outlined
                        block
                        large
                        @click="mixinOnClickUninstallApp(reloader, app_label)"
                    >
                        <v-icon
                            size="20"
                            class="mr-1"
                            aria-hidden="true"
                        >mdi-delete</v-icon>
                        Uninstall
                    </v-btn>

                    <v-btn
                        v-if="app_is_in_free_trial"
                        color="primary"
                        class="my-2"
                        outlined
                        block
                        large
                        @click="mixinOnClickCancelAppTrial(reloader, app_label)"
                    >
                        <v-icon
                            size="20"
                            class="mr-1"
                            aria-hidden="true"
                        >mdi-cancel</v-icon>
                        Cancel Free Trial
                    </v-btn>
                </template>

                <template v-else>
                    <v-alert
                        type="error"
                        dense
                        outlined
                        class="mt-4"
                    >
                        <div class="text-body-2">
                            <strong>Admin Access Required:</strong>
                            Only organization administrators can install or update apps. Please contact your
                            organization admin to proceed.
                        </div>
                    </v-alert>
                </template>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import AppInstallation from '@/mixins/app-installation.js';
import AppDerived from '@/mixins/app-derived.js';
import AppInstallBtn from '@/components/app-store/app-install-btn.vue';

export default {
    mixins: [AppInstallation, AppDerived],

    components: {
        AppInstallBtn,
    },

    data() {
        return {
            show_install_dialog: false,
        };
    },

    props: {
        app: {
            type: Object,
            required: true,
        },

        reloader: {
            type: Function,
            default: function () { },
        },
    },

    computed: {
        installed_time_pretty() {
            if (!this.app?.installed_time) {
                return '';
            }
            return this.$rc.pretty(this.app.installed_time, 'date');
        },
    }
};
</script>

<style lang="scss" scoped>
.sticky-card {
    position: sticky;
    top: 58px; // header height + breathing room
    left: 0;
}

.avatar-image {
    border: 1px solid rgba(0, 0, 0, 0.1);
}

@media (max-width: 960px) {
    .sticky-card {
        position: static;
    }
}
</style>